/**
 * KpiTile — single metric block reused across the departments surface.
 *
 * Notion-strict chrome: no border, no shadow. Label + value stacked
 * with tight leading, optional trailing hint. The intent is to show
 * a number in human-readable form (e.g. "12" rather than "12.00") —
 * the formatting helper handles small / large counts uniformly.
 */
import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';

interface KpiTileProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  /** Tone applies to the *value*, not the label. */
  tone?: 'default' | 'success' | 'warning' | 'error' | 'muted';
  className?: string;
}

const toneClass: Record<NonNullable<KpiTileProps['tone']>, string> = {
  default: 'text-foreground',
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-destructive',
  muted: 'text-muted-foreground',
};

export function KpiTile({ label, value, hint, tone = 'default', className }: KpiTileProps) {
  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className={cn('text-[15px] font-semibold tabular-nums leading-tight', toneClass[tone])}>
        {value}
      </span>
      {hint ? (
        <span className="text-[11px] text-muted-foreground/80 leading-snug">{hint}</span>
      ) : null}
    </div>
  );
}

// ── Number formatters reused across the dashboard ───────────────────

/** Short integer count, "—" for null/undefined. */
export function fmtCount(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (!Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('en');
}

/** Coarse "x m ago" / "Just now" relative timestamp. */
export function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '—';
  const diff = (Date.now() - ts) / 1000;
  if (diff < 30) return 'just now';
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86_400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604_800) return `${Math.floor(diff / 86_400)}d ago`;
  return new Date(ts).toLocaleDateString('en', { month: 'short', day: 'numeric' });
}

/** Format an error rate (0..1) as a percentage with one decimal. */
export function fmtPercent(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '—';
  if (!Number.isFinite(rate)) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}
